/**
 * Agent Memory Service
 *
 * Thin wrapper around the workspace + user memory REST endpoints. Consumed by
 * TanStack Query hooks in `hooks/data/useMemory.ts`.
 */

import { apiService } from './api.service';

/**
 * When this client last changed the user's memories, on the monotonic
 * `performance.now()` clock (null = never), so a wall-clock step cannot
 * distort the age. Sent to the agent as `x-memory-changed-ms-ago` so its
 * per-instance bundle cache refetches instead of serving the pre-change fact
 * for one more turn.
 */
let lastMemoryChangeAt: number | null = null;

/** Milliseconds since the last confirmed memory change, or null if none. */
export function msSinceMemoryChange(): number | null {
  return lastMemoryChangeAt === null ? null : performance.now() - lastMemoryChangeAt;
}

/** Record a change once the server confirmed it; pass the response through. */
async function markOnSuccess<T extends { success: boolean }>(request: Promise<T>): Promise<T> {
  const res = await request;
  if (res.success) lastMemoryChangeAt = performance.now();
  return res;
}

export interface WorkspaceMemoryDoc {
  content: string;
  version: number;
  updatedAt: string | null;
  updatedBy: { id: string; name: string | null } | null;
  canEdit: boolean;
  complianceBlock: string | null;
  charCap: number;
  isDefault: boolean;
}

export interface UserMemoryDoc {
  content: string;
  version: number;
  updatedAt: string | null;
  charCap: number;
  isDefault: boolean;
}

/**
 * One fact Bower remembers about the acting user in this workspace (a Memory
 * Bank memory). `content` is the fact, the user's own data. `createdAt` is when
 * Bower learned it; `updatedAt` moves forward when the fact is edited in place
 * or Memory Bank consolidates it with something newer, so the UI shows a
 * neutral "Updated" hint when `updatedAt > createdAt`, never "edited".
 */
export interface PersonalMemory {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  /**
   * False when Bower keeps the fact but does not use it in conversations (only
   * research focus, methods and lab conventions are used). Absent from an
   * older backend, which is read as used.
   */
  applied?: boolean;
}

class MemoryService {
  async getWorkspaceMemory() {
    return apiService.get<WorkspaceMemoryDoc>('/workspace/memory');
  }

  async saveWorkspaceMemory(content: string, version: number) {
    return markOnSuccess(
      apiService.put<WorkspaceMemoryDoc>('/workspace/memory', { content, version }),
    );
  }

  async getUserMemory() {
    return apiService.get<UserMemoryDoc>('/users/me/memory');
  }

  async saveUserMemory(content: string, version: number) {
    return markOnSuccess(apiService.put<UserMemoryDoc>('/users/me/memory', { content, version }));
  }

  /**
   * The acting user's own memories in this workspace ("What Bower remembers").
   * Gated server-side (never in a PHI workspace): a `PERSONALIZATION_DISABLED`
   * 404 when off (surface hidden), which the hook treats as "nothing to show"
   * rather than an error.
   */
  async listExplicitMemories() {
    // `truncated`: the server hit its list cap, so some memories are not shown.
    // Absent from an older backend.
    return apiService.get<{ memories: PersonalMemory[]; truncated?: boolean }>(
      '/workspace/memory/explicit',
    );
  }

  /**
   * Edit one of the acting user's own memories in place (correct what Bower
   * learned). The server replaces the fact in Memory Bank, which re-embeds it
   * (nothing is embedded on our side), and returns the updated memory with
   * fresh provenance.
   */
  async updatePersonalMemory(id: string, content: string) {
    return markOnSuccess(
      apiService.patch<{ memory: PersonalMemory }>(
        `/workspace/memory/explicit/${encodeURIComponent(id)}`,
        { content },
      ),
    );
  }

  /** Delete one of the acting user's own memories from Memory Bank (permanent). */
  async erasePersonalMemory(id: string) {
    return markOnSuccess(
      apiService.delete<{ erased: boolean }>(
        `/workspace/memory/explicit/${encodeURIComponent(id)}`,
      ),
    );
  }
}

export const memoryService = new MemoryService();
